import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Input,
  message,
  Descriptions,
  Avatar,
  Badge,
  Typography,
  Space,
  Spin,
  Tag
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  MailOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/authService';
import { formatDate } from '../utils/format';

const { Title, Text } = Typography;

const Profile = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaNotification, setMfaNotification] = useState(null);
  const [resendIn, setResendIn] = useState(0);
  const resendTimer = useRef(null);
  const [formResetKey, setFormResetKey] = useState(0);

  // Function to get color for each role
  const getRoleColor = (roleName, isSuper) => {
    const roleNameLower = roleName?.toLowerCase();
    
    // Super admin roles
    if (isSuper || roleNameLower?.includes('super')) {
      return 'magenta'; // Super Administrator
    }
    
    // Specific role colors based on API response
    switch (roleNameLower) {
      case 'admin':
        return 'cyan'; // Administrator
      case 'customer-support':
        return 'blue'; // Customer Support
      case 'super-admin':
        return 'magenta'; // Super Administrator
      default:
        return 'blue'; // Default fallback for any new roles
    }
  };

  // React Query for profile data
  const {
    data: userData,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: ['admin-profile'],
    queryFn: async () => {
      const response = await api.get('/admin/user-profile');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // React Query for user roles
  const {
    data: userRoles,
    isLoading: rolesLoading,
  } = useQuery({
    queryKey: ['user-roles', userData?.id],
    queryFn: async () => {
      if (!userData?.id) return [];
      const response = await api.get(`/admin/user-roles?user_id=${userData.id}`);
      return response.data.user_roles || [];
    },
    enabled: !!userData?.id, // Only run when we have user ID
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Deduplicate and select a single display role
  const rolesToShow = React.useMemo(() => {
    if (!userRoles || userRoles.length === 0) return [];

    // Deduplicate by lowercase name/display_name
    const uniqueByName = new Map();
    userRoles.forEach((role) => {
      const key = (role.display_name || role.name || '').toLowerCase();
      if (key && !uniqueByName.has(key)) uniqueByName.set(key, role);
    });
    let deduped = Array.from(uniqueByName.values());

    // If both super and non-super exist, prefer non-super
    const hasSuper = deduped.some((r) => r.is_super || /super/.test((r.name || '').toLowerCase()));
    if (deduped.length > 1 && hasSuper) {
      const nonSuper = deduped.filter((r) => !(r.is_super || /super/.test((r.name || '').toLowerCase())));
      if (nonSuper.length > 0) deduped = nonSuper;
    }

    // Show only one role tag
    return deduped.slice(0, 1);
  }, [userRoles]);

  // Mutation for password update
  const passwordUpdateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.patch('/admin/user-profile', data);
      return response;
    },
    onSuccess: (response) => {
      // Check for successful password update - either 204 status OR empty response body
      const isSuccessfulUpdate = response.status === 204 || 
        (response.status === 200 && (!response.data || Object.keys(response.data).length === 0));

      if (isSuccessfulUpdate) {
        message.success('Password updated successfully');
        setMfaStep(false);
        setMfaToken(null);
        setMfaNotification(null);
        setResendIn(0);
        form.resetFields();
        setFormResetKey(prev => prev + 1);
        // Invalidate and refetch profile data
        queryClient.invalidateQueries(['admin-profile']);
      } else if (response.data && response.data.token_identifier) {
        // MFA required
        setMfaStep(true);
        setMfaToken(response.data.token_identifier);
        setMfaNotification(response.data.notification_id);
        if (response.data.resend_in) {
          const now = Date.now();
          setResendIn(Math.max(Math.floor((response.data.resend_in - now) / 1000), 0));
        }
        message.info('Enter the OTP sent to your email to complete password update.');
      }
    },
    onError: (error) => {
      if (error.response && error.response.data && error.response.data.token_identifier) {
        setMfaStep(true);
        setMfaToken(error.response.data.token_identifier);
        setMfaNotification(error.response.data.notification_id);
        if (error.response.data.resend_in) {
          const now = Date.now();
          setResendIn(Math.max(Math.floor((error.response.data.resend_in - now) / 1000), 0));
        }
        message.info('Enter the OTP sent to your email to complete password update.');
      } else {
        message.error('Failed to update password');
      }
    },
    onSettled: () => setPasswordLoading(false),
  });

  useEffect(() => {
    if (mfaStep && resendIn > 0) {
      resendTimer.current = setInterval(() => {
        setResendIn(prev => {
          if (prev <= 1) {
            clearInterval(resendTimer.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(resendTimer.current);
  }, [mfaStep, resendIn]);

  const onPasswordSubmit = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('New passwords do not match');
      return;
    }
    setPasswordLoading(true);
    const body = {
      action: 'reset-password',
      old_password: values.currentPassword,
      new_password: values.newPassword,
      ...(mfaStep && mfaToken ? { mfa: { otp: values.otp, token_identifier: mfaToken } } : {})
    };
    passwordUpdateMutation.mutate(body);
  };

  const handleResendOTP = async () => {
    setPasswordLoading(true);
    try {
      const values = form.getFieldsValue();
      const body = {
        action: 'reset-password',
        old_password: values.currentPassword,
        new_password: values.newPassword
      };
      const response = await api.patch('/admin/user-profile', body);
      if (response.data && response.data.token_identifier) {
        setMfaToken(response.data.token_identifier);
        setMfaNotification(response.data.notification_id);
        if (response.data.resend_in) {
          const now = Date.now();
          setResendIn(Math.max(Math.floor((response.data.resend_in - now) / 1000), 0));
        }
        message.success('A new OTP has been sent to your email.');
      }
    } catch (error) {
      message.error('Failed to resend OTP');
    }
    setPasswordLoading(false);
  };

  if (profileLoading) {
    return <div style={{ textAlign: 'center', padding: 48 }}><Spin size="large" /></div>;
  }
  if (profileError) {
    return <div style={{ textAlign: 'center', padding: 48 }}><Text type="danger">Failed to fetch profile</Text></div>;
  }
  if (!userData) {
    return null;
  }

  return (
    <div style={{ padding: '24px 16px' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0, color: '#1a1a1a' }}>My Profile</Title>
        </div>
        <Row gutter={[24, 24]}>
          {/* Admin Information Card */}
          <Col span={24} lg={12}>
            <Card 
              style={{
                boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px 0 rgba(0,0,0,0.02)',
                borderRadius: '8px',
                height: '100%'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  padding: '16px',
                  background: '#f5f5f5',
                  borderRadius: '8px'
                }}>
                  <div style={{ position: 'relative' }}>
                    <Avatar 
                      size={88}
                      icon={<UserOutlined />}
                      style={{
                        backgroundColor: '#1890ff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                    {/* No roles badge, just show avatar */}
                  </div>
                  <div style={{ marginLeft: '24px' }}>
                    <Title level={3} style={{ margin: 0, color: '#262626' }}>{userData.firstname} {userData.lastname}</Title>
                    <Text type="secondary" style={{ fontSize: '14px' }}>{userData.email}</Text>
                  </div>
                </div>
                <Descriptions 
                  bordered
                  column={1}
                  size="middle"
                  labelStyle={{ 
                    width: '180px',
                    backgroundColor: '#fafafa'
                  }}
                  contentStyle={{
                    backgroundColor: '#ffffff'
                  }}
                >
                  <Descriptions.Item label="Full Name">{userData.firstname} {userData.lastname}</Descriptions.Item>
                  <Descriptions.Item label="Email">{userData.email}</Descriptions.Item>
                  <Descriptions.Item label="Phone Number">{userData.phone_number || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    {userData.status ? userData.status.charAt(0).toUpperCase() + userData.status.slice(1) : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Activated">{userData.is_activated ? 'Yes' : 'No'}</Descriptions.Item>
                  {rolesToShow && rolesToShow.length > 0 && (
                    <Descriptions.Item label="Role">
                      <Space wrap>
                        {rolesToShow.map((role, idx) => (
                          <Tag key={idx} color={getRoleColor(role.name, role.is_super)}>
                            {role.display_name || role.name}
                          </Tag>
                        ))}
                      </Space>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Created">{formatDate(userData.created)}</Descriptions.Item>
                </Descriptions>
              </Space>
            </Card>
          </Col>
          {/* Change Password Card */}
          <Col span={24} lg={12}>
            <Card 
              style={{
                boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px 0 rgba(0,0,0,0.02)',
                borderRadius: '8px',
                height: '100%'
              }}
              bodyStyle={{ padding: '24px' }}
              title={
                <Space size="middle">
                  <LockOutlined style={{ color: '#1890ff' }} />
                  <Title level={4} style={{ margin: 0 }}>Change Password</Title>
                </Space>
              }
            >
              <Form
                key={formResetKey}
                form={form}
                layout="vertical"
                onFinish={onPasswordSubmit}
                style={{ maxWidth: '100%' }}
              >
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <Form.Item
                    name="currentPassword"
                    label={<Text strong>Current Password</Text>}
                    rules={[
                      { required: true, message: 'Please input your current password!' }
                    ]}
                  >
                    <Input.Password 
                      prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                      placeholder="Enter current password"
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item
                    name="newPassword"
                    label={<Text strong>New Password</Text>}
                    rules={[
                      { required: true, message: 'Please input your new password!' },
                      { min: 8, message: 'Password must be at least 8 characters!' }
                    ]}
                    extra={<Text type="secondary">Password must be at least 8 characters long</Text>}
                  >
                    <Input.Password 
                      prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                      placeholder="Enter new password"
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item
                    name="confirmPassword"
                    label={<Text strong>Confirm New Password</Text>}
                    rules={[
                      { required: true, message: 'Please confirm your new password!' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('The two passwords do not match!'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password 
                      prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                      placeholder="Confirm new password"
                      size="large"
                    />
                  </Form.Item>
                  {mfaStep && (
                    <Form.Item
                      name="otp"
                      label={<Text strong>OTP</Text>}
                      rules={[
                        { required: true, message: 'Please input the OTP sent to your email!' }
                      ]}
                    >
                      <Input 
                        prefix={<SafetyCertificateOutlined style={{ color: '#bfbfbf' }} />}
                        placeholder="Enter OTP"
                        size="large"
                      />
                    </Form.Item>
                  )}
                  {mfaStep && (
                    <Form.Item>
                      <Button
                        type="link"
                        onClick={handleResendOTP}
                        disabled={resendIn > 0 || passwordLoading}
                        style={{ padding: 0 }}
                      >
                        {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
                      </Button>
                    </Form.Item>
                  )}
                  <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
                    <Button 
                      type="primary" 
                      htmlType="submit"
                      size="large"
                      style={{
                        width: '100%',
                        height: '44px'
                      }}
                      loading={passwordLoading}
                    >
                      {mfaStep ? 'Verify OTP & Update Password' : 'Update Password'}
                    </Button>
                  </Form.Item>
                </Space>
              </Form>
            </Card>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default Profile;